import { useSyncExternalStore } from "react";

// The current time, re-rendering every `intervalMs`, so "3 min ago"
// labels stay true on pages that do not poll (Deliverables, Memories,
// Vault). One shared timer per interval however many components ask,
// refreshed immediately when the tab becomes visible again.
//
// `useNow(0)` returns a stable Date and never re-renders - handy for a
// component that only sometimes shows a relative time (hooks cannot be
// conditional).

interface Clock {
  now: Date;
  listeners: Set<() => void>;
  timer: ReturnType<typeof setInterval> | null;
}

const clocks = new Map<number, Clock>();
const frozen = new Date();

function clockFor(intervalMs: number): Clock {
  let clock = clocks.get(intervalMs);
  if (!clock) {
    clock = { now: new Date(), listeners: new Set(), timer: null };
    clocks.set(intervalMs, clock);
  }
  return clock;
}

function tick(clock: Clock) {
  clock.now = new Date();
  clock.listeners.forEach((fn) => fn());
}

function onVisible() {
  if (document.visibilityState !== "visible") return;
  clocks.forEach((clock) => {
    if (clock.listeners.size) tick(clock);
  });
}

let visibilityHooked = false;

function subscribe(intervalMs: number, listener: () => void): () => void {
  if (intervalMs <= 0) return () => undefined;
  const clock = clockFor(intervalMs);
  clock.listeners.add(listener);
  if (!clock.timer) {
    // A clock that sat idle is stale; catch up before the first tick.
    if (Date.now() - clock.now.getTime() >= intervalMs) clock.now = new Date();
    clock.timer = setInterval(() => tick(clock), intervalMs);
  }
  if (!visibilityHooked && typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisible);
    visibilityHooked = true;
  }
  return () => {
    clock.listeners.delete(listener);
    if (!clock.listeners.size && clock.timer) {
      clearInterval(clock.timer);
      clock.timer = null;
    }
  };
}

// Stable subscribe/snapshot functions per interval: an inline arrow would
// make React resubscribe on every render, and a component re-rendering
// faster than the interval (a 5 s poll) would keep resetting the timer.
const bound = new Map<number, { sub: (l: () => void) => () => void; snap: () => Date }>();

function boundFor(intervalMs: number) {
  let b = bound.get(intervalMs);
  if (!b) {
    b = {
      sub: (listener) => subscribe(intervalMs, listener),
      snap: () => {
        if (intervalMs <= 0) return frozen;
        const clock = clockFor(intervalMs);
        // Nobody has been listening: the stored time is stale. Catch up
        // before the first render, not after it ("just now" for an item
        // that is an hour old). Stable across the calls of one render.
        if (!clock.timer && Date.now() - clock.now.getTime() >= intervalMs) clock.now = new Date();
        return clock.now;
      },
    };
    bound.set(intervalMs, b);
  }
  return b;
}

const serverSnap = () => frozen;

/** Current time, updated every `intervalMs` (default one minute; 0 = never). */
export function useNow(intervalMs = 60_000): Date {
  const b = boundFor(Math.max(0, Math.round(intervalMs)));
  return useSyncExternalStore(b.sub, b.snap, serverSnap);
}
